import { useMemo, useCallback } from 'react';
import { useMutation, InfiniteData, useInfiniteQuery } from 'react-query';
import produce from 'immer';

import { request, useCurrentUserId, toastError } from '@/utils';

import { FeedArticleDetail } from '../types';
import { transAuthor } from '../utils';
import { setQueryAllFeedDetailByArticleId } from './setQuery';

export const useFeedVote = (articleId: number, pollId: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    (optionIndex: number) =>
      request.post<{ is_success: boolean; inner_id: number }>('/feed/poll/vote', {
        id: pollId,
        option_index: optionIndex,
      }),
    {
      onSuccess: (res, optionIndex: number) => {
        const recipe = (pre?: FeedArticleDetail) => {
          if (pre?.article?.poll) {
            pre.article.poll.is_voted = true;
            pre.article.poll.voted_option_index = optionIndex;
            pre.article.poll.vote_count = (pre.article.poll.vote_count || 0) + 1;
            pre.article.poll.vote_stats = {
              ...pre.article.poll.vote_stats,
              [optionIndex]: (pre.article.poll.vote_stats?.[optionIndex] || 0) + 1,
            };
          }
        };

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId,
          recipe,
          includeQuote: true,
        });
      },
      onError(err: any) {
        toastError(err, 'failed to vote');
      },
    },
  );
};

const SEARCH_PAGE_LIMIT = 100;

export const useFeedVoters = (voteId: number, option_index?: number) => {
  const currentId = useCurrentUserId();
  const url = '/feed/poll/voters';

  const select = useCallback(
    (data: InfiniteData<{ users: GeneralAccount[]; total_count: number }>) => {
      return produce(data, draft => {
        draft.pages.forEach(page =>
          page.users?.forEach(x => {
            x._author = transAuthor({ user: x });
          }),
        );
      });
    },
    [],
  );

  const queryKey = useMemo(
    () => [url, voteId, currentId, option_index],
    [url, voteId, currentId, option_index],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    queryKey,
    async ({ pageParam }) => {
      const params: Record<string, number | string | boolean> = {
        limit: SEARCH_PAGE_LIMIT,
        start: 0,
        id: voteId,
      };

      if (option_index) {
        params.option_index = option_index;
      }

      if (pageParam) {
        Object.assign(params, pageParam);
      }

      return request.get<{ users: GeneralAccount[]; total_count: number }>(url, params);
    },
    {
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const length = lastPage.users?.length;
        if (length >= SEARCH_PAGE_LIMIT) {
          return {
            start: pages.length * SEARCH_PAGE_LIMIT,
          };
        }
        // Return false means no next page
        return false;
      },
      select,
      enabled: !!voteId,
      onError(err: any) {
        toastError(err);
      },
    },
  );

  const result = useMemo(
    () => ({
      list: data?.pages?.flatMap(x => x.users),
      totalCount: data?.pages[0]?.total_count,
    }),
    [data],
  );

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};
